/**
 * v-latex 指令（移动端，保守渲染策略）
 *
 * 用法与 v-html 一致：先把绑定值作为 HTML 渲染到元素内部。
 * 只有内容明显是「简单文本 + LaTeX 公式分隔符」时，才会调用 KaTeX
 * auto-render；只要看起来像复杂富文本（Word / TinyMCE 粘贴产生的
 * table / inline style / 已有公式产物等），就完全跳过 KaTeX，
 * 让原始 HTML 自己排版，避免破坏题库历史数据的展示。
 *
 * 性能策略：
 * 1. 字符串相同则跳过，避免父组件重渲染时的无谓重排。
 * 2. 不含公式分隔符的内容直接跳过 KaTeX。
 * 3. 渲染请求统一进入全局队列，按帧批量小批次处理。
 * 4. IntersectionObserver 懒渲染：元素接近视口时才入队。
 *
 * 注意：KaTeX CSS 已在 src/scss/index.scss 通过 `@import '~katex/dist/katex.min.css'`
 * 打入 bundle，这里不再重复 import。
 */

import Vue, { DirectiveOptions } from 'vue'
// @ts-ignore - 子路径无 TS 类型；webpack 4 不识别 katex package.json 的 exports 字段，必须用直接路径
import renderMathInElement from 'katex/dist/contrib/auto-render'
// 把 Shell/普通 $ 转义掉，避免 echo $PATH、$(date) 等被 KaTeX 误吞为公式
// 这里走 'html' 模式：把"非公式"的 $ 包成 <span class="vlsd">$</span>，
// 利用 KaTeX renderElem 不跨 element 合并 text 节点的特性来阻止配对。
import { escapeNonMathDollars, SKIP_DOLLAR_CLASS } from '@/utils/latex/escapeShellDollars'

interface RenderMathDelimiter {
  left: string
  right: string
  display: boolean
}

interface RenderMathOptions {
  delimiters?: RenderMathDelimiter[]
  ignoredTags?: string[]
  ignoredClasses?: string[]
  throwOnError?: boolean
  errorCallback?: (msg: string, err: Error) => void
}

const DEFAULT_OPTIONS: RenderMathOptions = {
  delimiters: [
    { left: '$$', right: '$$', display: true },
    { left: '\\[', right: '\\]', display: true },
    { left: '\\(', right: '\\)', display: false },
    { left: '$', right: '$', display: false }
  ],
  // 这些标签内的内容多半是已排版好的富文本/已渲染的公式，二次扫描容易破坏布局。
  // 额外把 svg / math (MathML) / img / iframe 也加进来，让"复杂富文本"短路
  // 能从整段跳过 KaTeX 收窄到只跳过这些标签内部，外面的纯文本公式照常渲染。
  ignoredTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code', 'table', 'svg', 'math', 'img', 'iframe'],
  // SKIP_DOLLAR_CLASS（'vlsd'）是 escapeNonMathDollars('html') 包裹"非公式 $"用的兜底类，
  // 加在这里作为防御性双保险——即使 splitAtDelimiters 越过 element 边界，KaTeX 也会跳过。
  ignoredClasses: ['katex', 'katex-display', 'MathJax', 'MathJax_Display', 'mq-math-mode', SKIP_DOLLAR_CLASS],
  throwOnError: false,
  errorCallback() { /* 静默：避免脏数据刷屏 */ }
}

const MAX_PER_FRAME = 4

const RENDERED_CLASS = 'v-latex-rendered'
const SKIPPED_CLASS = 'v-latex-skipped'

const FORMULA_DELIMITER_RE = /\$\$|\$|\\\(|\\\[/

// 命中以下任一特征就认为是「已经包含预渲染公式产物的富文本」，整段跳过 KaTeX，
// 避免对已经渲染好的 KaTeX / MathJax / Wiris / MathML DOM 再走一遍 auto-render
// 把它们的版面打散：
//   - <math>：MathML 根节点；
//   - class 含 katex / MathJax / mjx- / mq-math-mode / kity / wiris / Wirisformula；
//   - data-latex / data-mathml / data-wiris / data-formula 这些"公式源已被另一套
//     系统接管"的标记属性。
//
// 早期版本还把 <table>/<tbody>/<svg>/<img>/<iframe> 以及 mso-/font-family/
// position:absolute 等 inline 样式纳入短路，但这些只是 Word/TinyMCE 粘贴的常见
// 标签，本身并不代表"公式已经渲染过"。一旦题解里出现一个普通表格，整段题面的
// $A^{-1}$、$\begin{bmatrix}...\end{bmatrix}$ 就再也不渲染，反而是更严重的 bug。
// 现在依赖 ignoredTags（已包含 table/svg/math/img/iframe）做精准跳过，KaTeX 只会
// 跳过这些节点的内部，外层文本里的 $...$ 仍能正常配对成公式。
const COMPLEX_RICHTEXT_RE = /<\s*math\b|class\s*=\s*["'][^"']*\b(katex|MathJax|mjx-|mq-math-mode|kity|wiris|Wirisformula)\b|data-(latex|mathml|wiris|formula)\s*=/i

// 内容是否「可能含有公式分隔符」
function mayContainFormula(html: string): boolean {
  if (!html) return false
  return FORMULA_DELIMITER_RE.test(html)
}

// 内容是否属于「不再尝试公式渲染」的复杂富文本
function isComplexRichText(html: string): boolean {
  if (!html) return false
  return COMPLEX_RICHTEXT_RE.test(html)
}

interface LatexElement extends HTMLElement {
  __vLatexLast__?: string
  __vLatexPending__?: boolean
}

const renderQueue: LatexElement[] = []
let flushScheduled = false

function scheduleFlush() {
  if (flushScheduled) return
  flushScheduled = true

  const tick = () => {
    flushScheduled = false
    let processed = 0
    while (processed < MAX_PER_FRAME && renderQueue.length > 0) {
      const el = renderQueue.shift() as LatexElement
      if (!el || !el.__vLatexPending__) continue
      el.__vLatexPending__ = false
      if (!el.isConnected) continue
      try {
        renderMathInElement(el, DEFAULT_OPTIONS as any)
      } catch { /* 静默 */ }
      processed++
    }
    if (renderQueue.length > 0) scheduleFlush()
  }

  const w = window as any
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(tick, { timeout: 200 })
  } else {
    requestAnimationFrame(tick)
  }
}

function enqueue(el: LatexElement) {
  if (el.__vLatexPending__) return
  el.__vLatexPending__ = true
  renderQueue.push(el)
  scheduleFlush()
}

const observer: IntersectionObserver | null =
  typeof window !== 'undefined' && typeof window.IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const el = entry.target as LatexElement
              observer!.unobserve(el)
              enqueue(el)
            }
          }
        },
        { rootMargin: '200px 0px' }
      )
    : null

function requestRender(el: LatexElement) {
  if (observer) observer.observe(el)
  else enqueue(el)
}

function renderLatex(el: LatexElement, value: any) {
  // 先做一次 Shell/普通 $ 转义，再走原有渲染流程；纯文本和不含 $ 的输入是 O(1)。
  // mode='html'：用 <span class="vlsd">$</span> 包"非公式 $"，避免 \$ 在 innerHTML
  // 里以字面反斜杠的形态显示出来。
  const html = escapeNonMathDollars(value == null ? '' : String(value), 'html')
  if (el.__vLatexLast__ === html) return
  el.__vLatexLast__ = html

  el.classList.add(RENDERED_CLASS)
  el.innerHTML = html

  const hasDelimiter = mayContainFormula(html)
  const isComplex = isComplexRichText(html)

  // 关键：复杂富文本 / 没有公式分隔符的内容，全部短路，绝不调用 KaTeX。
  if (!hasDelimiter || isComplex) {
    el.classList.add(SKIPPED_CLASS)
    if (observer) observer.unobserve(el)
    return
  }

  el.classList.remove(SKIPPED_CLASS)
  requestRender(el)
}

const latexDirective: DirectiveOptions = {
  bind(el, binding) {
    renderLatex(el as LatexElement, binding.value)
  },
  update(el, binding) {
    renderLatex(el as LatexElement, binding.value)
  },
  unbind(el) {
    const target = el as LatexElement
    target.__vLatexPending__ = false
    if (observer) observer.unobserve(target)
  }
}

Vue.directive('latex', latexDirective)

export default latexDirective
